<template>
  <CContainer fluid>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader>
            Files Manager - {{ category }}
            <CButton color="secondary" size="sm" class="float-right" @click="$router.push('/dev_page/files-manager')">
              Back to Categories
            </CButton>
          </CCardHeader>
          <CCardBody>
            <CRow class="mb-2">
              <CCol sm="2">
                <label>Format</label>
                <select class="form-control" v-model="filters.format">
                  <option value="">All</option>
                  <option v-for="f in knownFormats" :key="f" :value="f">{{ f }}</option>
                </select>
              </CCol>
              <CCol sm="2">
                <label>Min Size (KB)</label>
                <CInput v-model="filters.min_size_kb" type="number" placeholder="e.g. 500" />
              </CCol>
              <CCol sm="2">
                <label>Max Size (KB)</label>
                <CInput v-model="filters.max_size_kb" type="number" placeholder="e.g. 5000" />
              </CCol>
              <CCol sm="2">
                <label>Usage</label>
                <select class="form-control" v-model="filters.usage">
                  <option value="">All</option>
                  <option value="used">Used</option>
                  <option value="orphan">Orphan</option>
                  <option value="unknown">Unknown</option>
                </select>
              </CCol>
              <CCol sm="4" class="d-flex align-items-end">
                <CButton color="info" class="mr-2" @click="applyFilter">Apply</CButton>
                <CButton color="secondary" @click="resetFilter">Reset</CButton>
              </CCol>
            </CRow>

            <div class="table-responsive">
              <table class="table table-bordered table-sm">
                <thead>
                  <tr>
                    <th>Preview</th>
                    <th>Filename</th>
                    <th>Format</th>
                    <th>Size</th>
                    <th>Dimensions</th>
                    <th>Modified</th>
                    <th>Usage</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rows" :key="row.filename">
                    <td>
                      <img
                        v-if="isImage(row.format)"
                        :src="fileUrl(row.filename)"
                        style="max-height:40px;max-width:60px;object-fit:contain"
                      />
                    </td>
                    <td class="text-break">{{ row.filename }}</td>
                    <td>{{ row.format }}</td>
                    <td>{{ formatBytes(row.size) }}</td>
                    <td>
                      <span v-if="row.dimensions">{{ row.dimensions.width }}x{{ row.dimensions.height }}</span>
                      <span v-else>-</span>
                    </td>
                    <td>{{ row.modified_at }}</td>
                    <td>
                      <CBadge :color="usageColor(row.usage_status)">{{ row.usage_status }}</CBadge>
                    </td>
                    <td>
                      <CButton color="info" size="sm" @click="openDetail(row)">Detail</CButton>
                    </td>
                  </tr>
                  <tr v-if="rows.length === 0">
                    <td colspan="8" class="text-center">No data</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="d-flex justify-content-between align-items-center">
              <div>Showing {{ total === 0 ? 0 : (page - 1) * perPage + 1 }} - {{ Math.min(page * perPage, total) }} of {{ total }}</div>
              <b-pagination
                v-model="page"
                :total-rows="total"
                :per-page="perPage"
                @change="loadData"
              ></b-pagination>
            </div>
          </CCardBody>
        </CCard>

        <CModal title="File Detail" :show.sync="detailModal" size="lg" color="info">
          <div v-if="selected">
            <CRow>
              <CCol sm="6">
                <p><b>Filename:</b> {{ selected.filename }}</p>
                <p><b>Format:</b> {{ selected.format }}</p>
                <p><b>Size:</b> {{ formatBytes(selected.size) }}</p>
                <p v-if="selected.dimensions"><b>Dimensions:</b> {{ selected.dimensions.width }}x{{ selected.dimensions.height }}</p>
                <p><b>Modified:</b> {{ selected.modified_at }}</p>
                <p><b>Usage:</b> <CBadge :color="usageColor(selected.usage_status)">{{ selected.usage_status }}</CBadge></p>
                <p v-if="selected.used_by"><b>Referenced by:</b> {{ JSON.stringify(selected.used_by) }}</p>
              </CCol>
              <CCol sm="6">
                <img :src="fileUrl(selected.filename)" style="max-width:100%;max-height:220px;object-fit:contain" />
              </CCol>
            </CRow>

          </div>

          <template #footer>
            <CButton color="secondary" @click="detailModal = false">Close</CButton>
          </template>
        </CModal>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import axios from 'axios'
import devService from '@/services/dev'

export default {
  props: {
    category: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      perPage: 25,
      filters: {
        format: '',
        min_size_kb: '',
        max_size_kb: '',
        usage: ''
      },
      knownFormats: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf'],
      detailModal: false,
      selected: null
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    buildParams() {
      return {
        page: this.page,
        per_page: this.perPage,
        format: this.filters.format,
        min_size: this.filters.min_size_kb ? this.filters.min_size_kb * 1024 : '',
        max_size: this.filters.max_size_kb ? this.filters.max_size_kb * 1024 : '',
        usage: this.filters.usage
      }
    },
    loadData() {
      devService.getFilesManagerFiles(this.category, this.buildParams())
        .then((res) => {
          this.rows = res.data || []
          this.total = res.total || 0
        })
        .catch((err) => {
          alert((err.response && err.response.data && err.response.data.message) || 'Failed to load files')
        })
    },
    applyFilter() {
      this.page = 1
      this.loadData()
    },
    resetFilter() {
      this.filters = { format: '', min_size_kb: '', max_size_kb: '', usage: '' }
      this.page = 1
      this.loadData()
    },
    openDetail(row) {
      this.selected = row
      this.detailModal = true

      devService.getFilesManagerFileDetail({ category: this.category, filename: row.filename })
        .then((res) => {
          this.selected = res.data
        })
        .catch(() => {})
    },
    fileUrl(filename) {
      return axios.defaults.baseURL + '/getfile/' + encodeURIComponent(this.category) + '/' + encodeURIComponent(filename)
    },
    isImage(format) {
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes((format || '').toLowerCase())
    },
    usageColor(status) {
      if (status === 'used') return 'success'
      if (status === 'orphan') return 'danger'
      return 'secondary'
    },
    formatBytes(bytes) {
      if (!bytes) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let i = 0
      let value = bytes
      while (value >= 1024 && i < units.length - 1) {
        value /= 1024
        i++
      }
      return value.toFixed(i === 0 ? 0 : 1) + ' ' + units[i]
    }
  }
}
</script>
